import axios, { AxiosRequestConfig, AxiosError } from 'axios';
import { useState, useEffect } from 'react';
import { getSessionId, setSessionId } from './sessionHeader';
import { getLanguageForApi } from '../util/getLanguageForApi';
import { getOrCreateAnonymousId } from '../anonymousId';
import { resolveWebApiUrl } from './resolveWebApiUrl';

/** API base URL for browser code. Import this rather than reading REACT_APP_WEB_API_URL directly. */
export const webApiUrl = resolveWebApiUrl(process.env.REACT_APP_WEB_API_URL!);
export const webApiClient = axios.create({
  baseURL: webApiUrl,
  headers: { 'x-api-key': process.env.REACT_APP_WEB_API_AUTH_KEY },
  withCredentials: true,
});

webApiClient.interceptors.request.use((config) => {
  const language = getLanguageForApi();
  config.headers['x-locale-region'] = language;
  // the shop and api can live on different registrable domains (fitracking, per-dev staging), where
  // the ajs_anonymous_id cookie cannot follow; the server reads this param before the cookie.
  config.params = { ...config.params, lng: language, anonymousId: getOrCreateAnonymousId() };
  const sid = getSessionId();
  if (sid) {
    config.headers['x-session-id'] = sid;
  }
  return config;
});

webApiClient.interceptors.response.use((response) => {
  if (response.data?.sessionId) {
    setSessionId(response.data.sessionId);
  }
  return response;
});

export function useAxiosGet<T = any>(url: string, config?: AxiosRequestConfig) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function performRequest() {
      try {
        const response = await webApiClient.get(url, config);
        setData(response.data);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    }
    performRequest();
  }, [url, config]);

  return { loading, error, data };
}

export function isAxiosError(err: any): err is AxiosError<any> {
  return !!err.isAxiosError;
}
